const MS_PER_DAY = 24 * 60 * 60 * 1000;

function parseDate(value) {
  if (value instanceof Date) return value;
  if (typeof value === 'string') return new Date(value);
  return null;
}

function startOfMonth(date) {
  return new Date(date.getFullYear(), date.getMonth(), 1);
}

function monthsBetween(from, to) {
  const a = startOfMonth(from);
  const b = startOfMonth(to);
  return (b.getFullYear() - a.getFullYear()) * 12 + (b.getMonth() - a.getMonth());
}

export function contributedToGoal(goal, transactions) {
  if (!goal) return 0;
  const startingAmount = Number(goal.startingAmount) || 0;
  if (!goal.linkedCategory) return startingAmount;
  const start = parseDate(goal.startDate);
  if (!start) return startingAmount;

  const contributed = transactions
    .filter((t) => t.category === goal.linkedCategory && t.type === 'income')
    .filter((t) => {
      const d = parseDate(t.date);
      return d && d >= start;
    })
    .reduce((sum, t) => sum + (Number(t.amount) || 0), 0);

  return startingAmount + contributed;
}

export function goalProgress(goal, transactions) {
  const contributed = contributedToGoal(goal, transactions);
  const target = Number(goal?.target) || 0;
  const remaining = Math.max(0, target - contributed);
  const percentComplete = target > 0 ? Math.min(100, (contributed / target) * 100) : 0;
  return { contributed, remaining, percentComplete };
}

export function requiredMonthlyContribution(goal, transactions, today = new Date()) {
  if (!goal || goal.archived) return 0;
  const { remaining } = goalProgress(goal, transactions);
  if (remaining <= 0) return 0;

  const target = parseDate(goal.targetDate);
  if (!target) return remaining;

  const now = parseDate(today) || new Date();
  if (target <= now) return Infinity;

  const months = monthsBetween(now, target);
  if (months <= 0) return remaining;
  return remaining / months;
}

function plannedContributedSoFar(goal, today) {
  const start = parseDate(goal.startDate);
  const target = parseDate(goal.targetDate);
  if (!start || !target || target <= start) return Number(goal.target) || 0;
  const total = Number(goal.target) || 0;
  const startingAmount = Number(goal.startingAmount) || 0;
  const elapsedMs = Math.max(0, today.getTime() - start.getTime());
  const totalMs = target.getTime() - start.getTime();
  const fraction = Math.min(1, elapsedMs / totalMs);
  return startingAmount + (total - startingAmount) * fraction;
}

export function goalStatus(goal, transactions, today = new Date()) {
  if (!goal) return 'on_track';
  const { contributed, remaining } = goalProgress(goal, transactions);
  if (remaining <= 0) return 'complete';

  const now = parseDate(today) || new Date();
  const target = parseDate(goal.targetDate);
  if (target && target < now) return 'overdue';

  const planned = plannedContributedSoFar(goal, now);
  const tolerance = Math.max(25, planned * 0.05);
  if (contributed + tolerance < planned) return 'behind';
  if (contributed > planned + tolerance) return 'ahead';
  return 'on_track';
}

export function urgencyScore(goal, transactions, today = new Date()) {
  if (!goal || goal.archived) return -1;
  const status = goalStatus(goal, transactions, today);
  if (status === 'complete') return -1;
  const now = parseDate(today) || new Date();
  const target = parseDate(goal.targetDate);
  const daysLeft = target ? Math.max(0, (target.getTime() - now.getTime()) / MS_PER_DAY) : 365;
  const statusWeight = { overdue: 0, behind: 1, on_track: 2, ahead: 3 }[status] ?? 2;
  return statusWeight * 10000 + daysLeft;
}
